import * as ExcelJS from "exceljs";
import { Checklist } from "models/Checklist";
import { Order } from "models/Order";
import { logoBase64 } from "./logo";

export const createExcel = async (order: Order, checklists: Checklist[], includeComments: boolean) => {
  const headings = [
    "Installation",
    "Artikel",
    "Benämning",
    "Serienummer",
    "MAC-adress",
    "MAC-adress (Wi-Fi)",
    "Nätverksuttag",
    "IP-adress",
    "Stöldmärkning",
    "Övrigt",
    ...(includeComments ? ["Kommentar"] : []),
  ];

  // Create a new workbook and worksheet
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet("Produkter");

  // Add logo to workbook
  const image = workbook.addImage({ base64: logoBase64, extension: "png" });

  // Add a top cell/header
  worksheet.getRow(1).height = 80;
  worksheet.mergeCells(1, 1, 1, headings.length);
  const topCell = worksheet.getCell(1, 1);
  topCell.alignment = {
    horizontal: "center",
    vertical: "middle",
    wrapText: true,
  };
  topCell.value = {
    richText: [
      { text: "Produktlista\n", font: { size: 20, bold: true } },
      { text: "Ordernr: ", font: { size: 16, bold: true } },
      { text: [order.ordernr, ...(order.projectOrders ?? [])].map((order) => order).join(", "), font: { size: 16, bold: false } },
    ],
  };
  topCell.border = { bottom: { style: "thin" }, right: { style: "thin" } };
  topCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "ffececec" },
  };

  // Add the second row with headings
  worksheet.addRow(headings).font = { bold: true, size: 13 };
  worksheet.getRow(2).height = 26;
  worksheet.getRow(2).eachCell((cell, colNumber) => {
    cell.border = { right: { style: "thin" }, bottom: { style: "thin" } };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "ffececec" },
    };
    cell.alignment = {
      horizontal: "center",
      vertical: "middle",
    };
    cell.value = headings[colNumber - 1]; // -1 is because colNumber is 1-based
  });

  // Place out logo in worksheet
  worksheet.addImage(image, {
    tl: { col: 0.15, row: 0.15 },
    ext: { width: 144, height: 60 },
    hyperlinks: {
      hyperlink: "https://avs.se/",
      tooltip: "https://avs.se/",
    },
  });

  // Style columns (width)
  worksheet.columns = [
    { key: "roomName", width: 25 },
    { key: "item", width: 25 },
    { key: "description", width: 40 },
    { key: "serial", width: 25 },
    { key: "macAddressWired", width: 25 },
    { key: "macAddressWifi", width: 25 },
    { key: "ethernetPort", width: 25 },
    { key: "ip", width: 25 },
    { key: "theftMarking", width: 25 },
    { key: "other", width: 40 },
    ...(includeComments ? [{ key: "comments", width: 50 }] : []),
  ];

  // Add the products data to the worksheet
  checklists.forEach((checklist) => {
    checklist.installedProducts.forEach((product, index) => {
      worksheet.addRow([
        checklist.roomName,
        product.item,
        product.description,
        product.serial,
        product.macAddressWired,
        product.macAddressWifi,
        product.ethernetPort,
        product.ip,
        product.theftMarking,
        product.other,
        // Only include comments in the first row of each checklist to avoid repetition,
        // since comments are per checklist, not per product
        ...(includeComments && index === 0
          ? [checklist.internalComments.map((comment) => comment.message).join(", ")]
          : []
        ),
      ]);

      product.children?.forEach((child) => {
        worksheet.addRow([
          checklist.roomName,
          child.item,
          child.description,
          child.serial,
          child.macAddressWired,
          child.macAddressWifi,
          child.ethernetPort,
          child.ip,
          child.theftMarking,
          child.other,
        ]);
      });
    });

    checklist.serialEntities.forEach((product, index) => {
      worksheet.addRow([
        checklist.roomName,
        product.model,
        product.maker,
        product.serialNumber,
        product.macAddress,
        product.macAddressWifi,
        product.networkSocket,
        product.ipAddress,
        product.theftProtection,
        product.other,
        // Add comments here if not previously added for installedProducts,
        // and only for the first serialEntity to avoid repetition
        ...(includeComments && index === 0 && checklist.installedProducts.length === 0
          ? [checklist.internalComments.map((comment) => comment.message).join(", ")]
          : []
        ),
      ]);
    });
  });

  return await workbook.xlsx.writeBuffer().then((buffer) => {
    return new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
  });
};
